import type { UploadFileStatus } from '../../../context/progressContext'
import type { UploadStatus } from './types'

export type UploadFilter = 'all' | UploadStatus

export type UploadVisibleRow = {
	item: UploadFileStatus
	depth: number
	hasChildren: boolean
	isExpanded: boolean
}

type GetVisibleUploadRowsParams = {
	items: UploadFileStatus[]
	filter: UploadFilter
	expandedIds: Set<string>
	collapsedIds: Set<string>
}

const orderFoldersFirst = (items: UploadFileStatus[]) =>
	[...items].sort(
		(first, second) =>
			Number(second.nodeType === 'folder') -
			Number(first.nodeType === 'folder'),
	)

export const getVisibleUploadRows = ({
	items,
	filter,
	expandedIds,
	collapsedIds,
}: GetVisibleUploadRowsParams): UploadVisibleRow[] => {
	const itemsById = new Map(
		items.filter((item) => item.id).map((item) => [item.id!, item]),
	)
	const childrenByParentId = new Map<string, UploadFileStatus[]>()
	const rootItems: UploadFileStatus[] = []

	for (const item of items) {
		if (!item.id) continue

		if (!item.parentStatusId || !itemsById.has(item.parentStatusId)) {
			rootItems.push(item)
			continue
		}

		const children = childrenByParentId.get(item.parentStatusId) || []
		children.push(item)
		childrenByParentId.set(item.parentStatusId, children)
	}

	const visibleById = new Map<string, boolean>()

	const hasVisibleDescendant = (
		item: UploadFileStatus,
		ancestors = new Set<string>(),
	): boolean => {
		if (!item.id || ancestors.has(item.id)) return false
		if (filter === 'all' || item.status === filter) return true

		const cached = visibleById.get(item.id)
		if (cached !== undefined) return cached

		const nextAncestors = new Set(ancestors).add(item.id)
		const isVisible = (childrenByParentId.get(item.id) || []).some((child) =>
			hasVisibleDescendant(child, nextAncestors),
		)

		visibleById.set(item.id, isVisible)
		return isVisible
	}

	const rows: UploadVisibleRow[] = []

	const addRows = (item: UploadFileStatus, depth: number) => {
		if (!item.id || !hasVisibleDescendant(item)) return

		const children = orderFoldersFirst(childrenByParentId.get(item.id) || [])
		const visibleChildren = children.filter((c) => hasVisibleDescendant(c))
		const hasChildren = children.length > 0
		const isFolder = item.nodeType === 'folder'
		const isAutoExpanded = filter !== 'all' && visibleChildren.length > 0
		const isExpanded =
			isFolder &&
			(expandedIds.has(item.id) ||
				(isAutoExpanded && !collapsedIds.has(item.id)))

		rows.push({ item, depth, hasChildren, isExpanded })

		if (isExpanded) {
			visibleChildren.forEach((child) => addRows(child, depth + 1))
		}
	}

	orderFoldersFirst(rootItems).forEach((item) => addRows(item, 0))

	return rows
}
